import { Component, createEffect, createSignal, onMount, Show, Index, type Accessor } from 'solid-js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { Message } from '../types/chat';
import { showToast } from '../services/toast';

interface ChatAreaProps {
  messages: Message[];
  isStreaming?: boolean;
  onSearchToggle?: (enabled: boolean) => void;
  onClearConversation?: () => void;
  showThinkingDefault?: boolean;
  expandThinkingWhileStreaming?: boolean;
  onImportConversation?: (messages: Message[]) => void;
  onRegenerateLast?: () => void;
  onEditLast?: (newText: string) => void;
}

export const ChatArea: Component<ChatAreaProps> = (props) => {
  let chatRef: HTMLElement | undefined;
  let intersectionObserver: IntersectionObserver | undefined;
  let sentinelEl: HTMLDivElement | null = null;
  const [shouldAutoScroll, setShouldAutoScroll] = createSignal(true);
  const [isAtBottom, setIsAtBottom] = createSignal(true);
  const [showSearch, setShowSearch] = createSignal(false);
  const [searchTerm, setSearchTerm] = createSignal('');
  const [searchResults, setSearchResults] = createSignal<number[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = createSignal(-1);
  const [showTimestamps, setShowTimestamps] = createSignal(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = createSignal(true);
  let fileInputRef: HTMLInputElement | undefined;
  // no-op placeholder removed

  onMount(() => {
    if (chatRef) {
      // Set up intersection observer for auto-scroll behavior
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsAtBottom(entry.isIntersecting);
            if (entry.isIntersecting) {
              setShouldAutoScroll(true);
            }
          });
        },
        { threshold: 0.8 }
      );

      // Create a sentinel element at the bottom
  sentinelEl = document.createElement('div');
  sentinelEl.style.height = '1px';
  sentinelEl.setAttribute('aria-hidden', 'true');
  chatRef.appendChild(sentinelEl);
  intersectionObserver.observe(sentinelEl);

      // Add mobile swipe gesture support
      let startY = 0;
      let startTime = 0;
      let isScrolling = false;

      const handleTouchStart = (e: TouchEvent) => {
        startY = e.touches[0].clientY;
        startTime = Date.now();
        isScrolling = false;
      };

      const handleTouchMove = (e: TouchEvent) => {
        if (!isScrolling) {
          const deltaY = Math.abs(e.touches[0].clientY - startY);
          if (deltaY > 10) {
            isScrolling = true;
          }
        }
      };

      const handleTouchEnd = (e: TouchEvent) => {
        if (!isScrolling) {
          const endY = e.changedTouches[0].clientY;
          const deltaY = startY - endY;
          const deltaTime = Date.now() - startTime;
          const velocity = Math.abs(deltaY) / deltaTime;

          // Quick swipe up to scroll to bottom
          if (deltaY > 50 && velocity > 0.5 && deltaTime < 300) {
            if (chatRef) {
              chatRef.scrollTop = chatRef.scrollHeight;
              setShouldAutoScroll(true);
            }
          }
          // Quick swipe down at top to refresh (future feature)
          else if (deltaY < -30 && velocity > 0.5 && deltaTime < 300 && chatRef?.scrollTop === 0) {
            // Could implement pull-to-refresh here in the future
            console.log('Pull to refresh gesture detected');
          }
        }
      };

      chatRef.addEventListener('touchstart', handleTouchStart, { passive: true });
      chatRef.addEventListener('touchmove', handleTouchMove, { passive: true });
      chatRef.addEventListener('touchend', handleTouchEnd, { passive: true });

      // Global keyboard: Ctrl+F to toggle search bar and focus input
      const handleGlobalKey = (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F')) {
          e.preventDefault();
          setShowSearch(true);
          setTimeout(() => {
            const input = (chatRef?.parentElement || document).querySelector('.search-input') as HTMLInputElement | null;
            input?.focus();
          }, 0);
        }
      };
      window.addEventListener('keydown', handleGlobalKey);

      return () => {
        if (chatRef) {
          chatRef.removeEventListener('touchstart', handleTouchStart);
          chatRef.removeEventListener('touchmove', handleTouchMove);
          chatRef.removeEventListener('touchend', handleTouchEnd);
        }
        window.removeEventListener('keydown', handleGlobalKey);
        if (intersectionObserver) {
          intersectionObserver.disconnect();
          intersectionObserver = undefined;
        }
        if (chatRef && sentinelEl) {
          try { chatRef.removeChild(sentinelEl); } catch {}
          sentinelEl = null;
        }
      };
    }
  });

  // Improved auto-scroll with intersection observer + near-bottom guard
  createEffect(() => {
    void props.messages.length; // track changes without creating an unused variable
    if (!chatRef) return;
    requestAnimationFrame(() => {
      if (!chatRef) return;
      const { scrollTop, scrollHeight, clientHeight } = chatRef;
      const nearBottom = (scrollHeight - scrollTop - clientHeight) < 120;
      if (autoScrollEnabled() && (shouldAutoScroll() || nearBottom || props.isStreaming)) {
        chatRef.scrollTop = scrollHeight;
        setShouldAutoScroll(true);
      }
    });
  });

  const handleScroll = () => {
    if (chatRef) {
      const { scrollTop, scrollHeight, clientHeight } = chatRef;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      if (autoScrollEnabled()) setShouldAutoScroll(isNearBottom);
    }
  };

  // Render a single message reactively; accept the item accessor so streaming updates propagate
  const renderMessage = (message: Accessor<Message>) => {
    // Configure marked for better security and accessibility
  marked.setOptions({ breaks: true, gfm: true });

    // Handle empty content to ensure bubble is visible
  const content = () => message().content.trim();
  const thinking = () => (message().thinking || '').trim();
    const htmlContent = () => {
      if (!content()) return '';
  const raw = marked.parse(content()) as string;
  return DOMPurify.sanitize(raw);
    };
    // Add copy buttons to code blocks after render
    let renderedRef: HTMLDivElement | undefined;
    createEffect(() => {
      // Track content changes to re-bind buttons
      void content();
      queueMicrotask(() => {
        if (!renderedRef) return;
        const blocks = renderedRef.querySelectorAll('pre');
        blocks.forEach((pre) => {
          if (pre.querySelector('.code-copy-btn')) return;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'code-copy-btn';
          btn.title = 'Copy code';
          btn.setAttribute('aria-label', 'Copy code');
          btn.textContent = '📋';
          btn.addEventListener('click', () => {
            const text = (pre as HTMLElement).innerText;
            navigator.clipboard?.writeText(text);
            showToast('Code copied', 'success');
          });
          pre.appendChild(btn);
        });
      });
    });
    const sourcesHtml = () => {
      const s = (message().sources || '').trim();
      if (!s) return '';
      const raw = marked.parse(s) as string;
      return DOMPurify.sanitize(raw);
    };
    
    // Debug logging for assistant messages
    // Note: keep lightweight to avoid console spam during streaming
    // if (message().role === 'assistant' && content()) {
    //   console.debug('Assistant content update:', content().substring(0, 30));
    // }
    
    return (
  <div role="group" aria-label={`${message().role} message`} data-message-id={message().id || ''}>
        {/* Sources first (collapsed by default) */}
        <Show when={message().role === 'assistant' && (message().sources || '').trim().length > 0}>
          <details class="thinking-panel">
            <summary class="thinking-summary">
              <span role="img" aria-hidden="true">🔗</span>
              Sources
            </summary>
            <div class="thinking-toolbar">
              <button
                type="button"
                class="thinking-copy-btn"
                title="Copy sources"
                aria-label="Copy sources"
                onClick={() => {
                  const s = (message().sources || '').trim();
                  if (s) {
                    navigator.clipboard?.writeText(s);
                    showToast('Sources copied', 'success');
                  }
                }}
              >
                📋 Copy
              </button>
            </div>
            <div class="thinking-content" innerHTML={sourcesHtml()} />
          </details>
        </Show>

        {/* Main message bubble with avatar */}
        <div class="bubble" role="document">
          <div class="bubble-header">
            <div class="avatar" aria-hidden="true">
              {message().role === 'user' ? '🧑' : message().role === 'assistant' ? '🤖' : '⚙️'}
            </div>
            <Show when={message().role === 'assistant' && (message().agentName || '').trim().length > 0}>
              <span class="agent-badge" title={(message().agentDiscipline ? `${message().agentName} • ${message().agentDiscipline}` : message().agentName) || ''}>
                {message().agentName}
              </span>
            </Show>
          </div>
          <Show when={content().length > 0} fallback={
            <span class="thinking-placeholder">
              <span class="thinking-text">Thinking</span>
              <span class="thinking-dots">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </span>
            </span>
          }>
            <div ref={(el) => (renderedRef = el)} innerHTML={htmlContent()}></div>
          </Show>
          <div class="message-toolbar">
            <button class="mini-btn" title="Copy" aria-label="Copy" onClick={() => {
              const full = [message().content, (message().sources ? `\n\nSources:\n${message().sources}` : ''), (message().thinking ? `\n\nThoughts:\n${message().thinking}` : '')].filter(Boolean).join('');
              navigator.clipboard?.writeText(full);
              showToast('Copied message', 'success');
            }}>📋</button>
            <Show when={message().role === 'assistant'}>
              <button class="mini-btn" title="Regenerate" aria-label="Regenerate" onClick={() => props.onRegenerateLast?.()}>🔁</button>
            </Show>
            <Show when={message().role === 'user'}>
              <button class="mini-btn" title="Edit and resend" aria-label="Edit and resend" onClick={() => {
                const next = prompt('Edit your last message:', message().content);
                if (typeof next === 'string' && next.trim()) {
                  props.onEditLast?.(next);
                }
              }}>✏️</button>
            </Show>
          </div>
        </div>

        {/* Thinking last */}
        <Show when={message().role === 'assistant' && thinking().length > 0}>
          <details class="thinking-panel" open={props.showThinkingDefault || (props.expandThinkingWhileStreaming && props.isStreaming)}>
            <summary class="thinking-summary">
              <span role="img" aria-hidden="true">🧩</span>
              Thought process
            </summary>
            <div class="thinking-toolbar">
              <button
                type="button"
                class="thinking-copy-btn"
                title="Copy thought process"
                aria-label="Copy thought process"
                onClick={() => navigator.clipboard?.writeText(thinking())}
              >
                📋 Copy
              </button>
            </div>
            <div class="thinking-content" innerHTML={(() => {
              const raw = marked.parse(thinking()) as string;
              return DOMPurify.sanitize(raw);
            })()} />
          </details>
        </Show>
      </div>
    );
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getMessageLabel = (message: Message) => {
    switch (message.role) {
      case 'user':
        return 'Your message';
      case 'assistant':
        return 'AI response';
      case 'system':
        return 'System message';
      default:
        return 'Message';
    }
  };

  const performSearch = (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      return;
    }

    const results: number[] = [];
    const searchLower = term.toLowerCase();

    props.messages.forEach((message, index) => {
      if (message.content.toLowerCase().includes(searchLower)) {
        results.push(index);
      }
    });

    setSearchResults(results);
    setCurrentSearchIndex(results.length > 0 ? 0 : -1);

    if (results.length > 0) {
      scrollToMessage(results[0]);
    }
  };

  const scrollToMessage = (messageIndex: number) => {
    const messageElement = chatRef?.querySelector(`[data-message-id="${props.messages[messageIndex]?.id || messageIndex}"]`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageElement.classList.add('highlight-search');
      setTimeout(() => {
        messageElement.classList.remove('highlight-search');
      }, 2000);
    }
  };

  const nextSearchResult = () => {
    const results = searchResults();
    if (results.length === 0) return;

    const nextIndex = (currentSearchIndex() + 1) % results.length;
    setCurrentSearchIndex(nextIndex);
    scrollToMessage(results[nextIndex]);
  };

  const prevSearchResult = () => {
    const results = searchResults();
    if (results.length === 0) return;

    const prevIndex = currentSearchIndex() === 0 ? results.length - 1 : currentSearchIndex() - 1;
    setCurrentSearchIndex(prevIndex);
    scrollToMessage(results[prevIndex]);
  };

  const handleSearchKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        prevSearchResult();
      } else {
        nextSearchResult();
      }
    } else if (e.key === 'Escape') {
      setShowSearch(false);
      setSearchTerm('');
      setSearchResults([]);
    }
  };

  return (
    <section class="chat-section" onDragOver={(e) => e.preventDefault()} onDrop={async (e) => {
      e.preventDefault();
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      try {
        if (file.type === 'application/json' || file.name.endsWith('.json')) {
          const text = await file.text();
          const data = JSON.parse(text);
          props.onImportConversation?.(data);
        }
      } catch {}
    }}>
      {/* Chat Controls */}
      <div class="chat-controls">
        <details class="pill-menu">
          <summary class="pill" aria-label="Open chat actions">Actions</summary>
          <div class="chat-actions">
          <button
            type="button"
            class="chat-control-btn"
            onClick={() => {
              setShowSearch(!showSearch());
              props.onSearchToggle?.(!showSearch());
            }}
            aria-label="Toggle search"
            title="Search conversation (Ctrl+F)"
          >
            <span role="img" aria-hidden="true">🔍</span>
            <span class="btn-text">Search</span>
          </button>
          <button
            type="button"
            class="chat-control-btn"
            onClick={() => props.onRegenerateLast?.()}
            aria-label="Regenerate last reply"
            title="Regenerate last AI reply"
          >
            <span role="img" aria-hidden="true">🔁</span>
            <span class="btn-text">Regenerate</span>
          </button>
          <button
            type="button"
            class="chat-control-btn"
            onClick={() => {
              try {
                const md = props.messages.map(m => {
                  const time = new Date(m.timestamp).toLocaleString();
                  const header = `### ${m.role} — ${time}`;
                  const body = m.content || '';
                  const parts = [header, '', body];
                  if ((m as any).sources) {
                    parts.push('', '#### Sources', (m as any).sources);
                  }
                  if ((m as any).thinking) {
                    parts.push('', '#### Thoughts', (m as any).thinking);
                  }
                  return parts.join('\n');
                }).join('\n\n');
                const blob = new Blob([md], { type: 'text/markdown' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `conversation-${new Date().toISOString().replace(/[:.]/g, '-')}.md`;
                a.click();
                URL.revokeObjectURL(url);
                showToast('Exported Markdown', 'success');
              } catch {}
            }}
            aria-label="Export as Markdown"
            title="Download conversation as Markdown"
          >
            <span role="img" aria-hidden="true">📝</span>
            <span class="btn-text">Export MD</span>
          </button>
          
          <button
            type="button"
            class="chat-control-btn"
            onClick={() => props.onClearConversation?.()}
            aria-label="Clear conversation"
            title="Clear all messages"
          >
            <span role="img" aria-hidden="true">🗑️</span>
            <span class="btn-text">Clear</span>
          </button>

          <button
            type="button"
            class="chat-control-btn"
            onClick={() => setShowTimestamps(!showTimestamps())}
            aria-label="Toggle timestamps"
            title="Show/hide timestamps"
          >
            <span role="img" aria-hidden="true">🕒</span>
            <span class="btn-text">Timestamps</span>
          </button>

          <button
            type="button"
            class="chat-control-btn"
            onClick={() => setAutoScrollEnabled(!autoScrollEnabled())}
            aria-label="Toggle auto-scroll"
            title="Enable/disable auto-scroll"
          >
            <span role="img" aria-hidden="true">{autoScrollEnabled() ? '📌' : '📍'}</span>
            <span class="btn-text">Auto-scroll</span>
          </button>

          {/* Export conversation */}
          <button
            type="button"
            class="chat-control-btn"
            onClick={() => {
              try {
                const blob = new Blob([JSON.stringify(props.messages, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `conversation-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
                a.click();
                URL.revokeObjectURL(url);
              } catch {}
            }}
            aria-label="Export conversation"
            title="Download conversation JSON"
          >
            <span role="img" aria-hidden="true">💾</span>
            <span class="btn-text">Export</span>
          </button>

          {/* Import conversation */}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            class="visually-hidden"
            aria-label="Import conversation JSON file"
            title="Import conversation JSON file"
            onChange={async (e) => {
              const file = e.currentTarget.files?.[0];
              if (!file) return;
              try {
                const text = await file.text();
                const data = JSON.parse(text);
                props.onImportConversation?.(data);
              } catch {}
              e.currentTarget.value = '';
            }}
          />
          <button
            type="button"
            class="chat-control-btn"
            onClick={() => fileInputRef?.click()}
            aria-label="Import conversation"
            title="Import conversation JSON"
          >
            <span role="img" aria-hidden="true">📥</span>
            <span class="btn-text">Import</span>
          </button>
          
          <div class="chat-info">
            <span class="message-count">
              {props.messages.length} message{props.messages.length !== 1 ? 's' : ''}
            </span>
          </div>
          </div>
        </details>

  <Show when={showSearch()}>
          <div class="search-bar" role="search">
            <div class="search-input-group">
              <input
                type="text"
                class="search-input"
                placeholder="Search messages..."
                value={searchTerm()}
                onInput={(e) => {
                  setSearchTerm(e.currentTarget.value);
                  performSearch(e.currentTarget.value);
                }}
                onKeyDown={handleSearchKeyDown}
                aria-label="Search messages"
                autofocus
              />
              <div class="search-controls">
                <button
                  type="button"
                  class="search-nav-btn"
                  onClick={prevSearchResult}
                  disabled={searchResults().length === 0}
                  aria-label="Previous result"
                  title="Previous (Shift+Enter)"
                >
                  ↑
                </button>
                <button
                  type="button"
                  class="search-nav-btn"
                  onClick={nextSearchResult}
                  disabled={searchResults().length === 0}
                  aria-label="Next result"
                  title="Next (Enter)"
                >
                  ↓
                </button>
                <button
                  type="button"
                  class="search-close-btn"
                  onClick={() => {
                    setShowSearch(false);
                    setSearchTerm('');
                    setSearchResults([]);
                  }}
                  aria-label="Close search"
                  title="Close (Escape)"
                >
                  ✕
                </button>
              </div>
            </div>
            <Show when={searchTerm().length > 0}>
              <div class="search-results-info">
                {searchResults().length > 0 ? (
                  <>
                    {currentSearchIndex() + 1} of {searchResults().length} results
                  </>
                ) : (
                  'No results found'
                )}
              </div>
            </Show>
          </div>
        </Show>
      </div>

      <main 
        id="chat" 
        ref={(el) => { chatRef = el; }}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-label="Conversation history"
        tabindex="0"
        class="chat-container"
        onKeyDown={(e) => {
          if (e.key === '/') {
            setShowSearch(true);
            // focus search after a tick
            setTimeout(() => {
              const input = (chatRef?.parentElement || document).querySelector('.search-input') as HTMLInputElement | null;
              input?.focus();
            }, 0);
            e.preventDefault();
          }
        }}
      >
      <div class="messages-container">
        <Index each={props.messages}>
          {(message, index) => (
            <article 
              class={`message ${message().role}`}
              role="article"
              aria-label={`${getMessageLabel(message())} from ${formatTimestamp(message().timestamp)}`}
              data-message-id={message().id || index}
            >
              <div class={`message-header ${showTimestamps() ? '' : 'visually-hidden'}`}>
                <span class="message-role">{getMessageLabel(message())}</span>
                <time 
                  class="message-time"
                  datetime={new Date(message().timestamp).toISOString()}
                >
                  {formatTimestamp(message().timestamp)}
                </time>
              </div>
              {renderMessage(message)}
              <button 
                class="message-actions-btn"
                type="button"
                aria-label={`Actions for ${getMessageLabel(message()).toLowerCase()}`}
                onClick={() => {
                  // Copy message content to clipboard
                  const full = [
                    message().content,
                    (message().sources ? `\n\nSources:\n${message().sources}` : ''),
                    (message().thinking ? `\n\nThoughts:\n${message().thinking}` : '')
                  ].filter(Boolean).join('');
                  navigator.clipboard?.writeText(full);
                }}
              >
                <span role="img" aria-hidden="true">📋</span>
                <span class="visually-hidden">Copy message</span>
              </button>
            </article>
          )}
        </Index>
  {/* Removed separate streaming indicator bubble; the assistant bubble shows a subtle thinking placeholder */}
      </div>
      
      {!isAtBottom() && (
        <button 
          class="scroll-to-bottom"
          type="button"
      onClick={() => {
            if (chatRef) {
              chatRef.scrollTop = chatRef.scrollHeight;
              setShouldAutoScroll(true);
        setAutoScrollEnabled(true);
            }
          }}
          aria-label="Scroll to bottom of conversation"
        >
          <span role="img" aria-hidden="true">⬇️</span>
          <span class="visually-hidden">New messages</span>
        </button>
      )}
      </main>
    </section>
  );
};